/**
 * VoucherActivityRow — one row in the drawer's activity stream on
 * `/petty-cash` (S30 activity list).
 *
 * Mirrors `prototype/cash/petty-cash.html ▸ .act-row` (the petty-cash variant
 * of the bank-account detail activity row): category icon chip + title +
 * sub-line (narration · time · operator) + signed amount + tail pill.
 *
 * The tail pill is one of:
 *   - "Posted" — normal flow
 *   - "Founder approved" — when `requiresFounderReview` is true (amber)
 *
 * Pure presentation; the route maps `cashMovements.list` rows into the
 * shape this component expects.
 *
 * @see prototype/cash/petty-cash.html `.act-row` (lines ~533–597)
 */
import { Link } from "@tanstack/react-router";
import { cn } from "@flint/ui";
import type { PettyCashCategory } from "../lib/categories.tsx";
import { CATEGORY_META } from "../lib/categories.tsx";
import { paiseToInr } from "../../../lib/format.ts";

export interface VoucherActivityRowProps {
  id: string;
  /** Friendly time like "14:22" — already formatted by the caller. */
  time: string;
  category: PettyCashCategory;
  /** Free-text `cash_movements.narration`. */
  narration: string;
  /** Amount in paise (always positive; rendered as outflow). */
  amountPaise: string;
  /** "Priya" / "Rakesh" — display name only. */
  postedBy: string;
  /** True when amount exceeded the company's approval threshold. */
  requiresFounderReview: boolean;
}

export function VoucherActivityRow({
  id,
  time,
  category,
  narration,
  amountPaise,
  postedBy,
  requiresFounderReview,
}: VoucherActivityRowProps) {
  const meta = CATEGORY_META[category];
  return (
    <Link
      to="/cash/petty-cash/$voucherId"
      params={{ voucherId: id }}
      className={cn(
        "relative grid gap-3 px-4 py-3 border-b border-line last:border-b-0 items-center",
        "grid-cols-[28px_minmax(0,1fr)_auto_auto]",
        "hover:bg-raised transition-colors no-underline text-inherit",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "absolute left-0 top-2 bottom-2 w-[3px] rounded-r-sm",
          requiresFounderReview ? "bg-warn" : "bg-bad",
        )}
      />
      <span
        aria-hidden
        className={cn(
          "inline-grid place-items-center w-7 h-7 rounded-md shrink-0",
          requiresFounderReview ? "bg-warn-50 text-warn" : "bg-bad-50 text-bad",
        )}
      >
        <svg
          width={14}
          height={14}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {meta.icon}
        </svg>
      </span>

      <div className="min-w-0">
        <p className="text-sm font-semibold text-ink mb-0.5">{meta.label}</p>
        <p className="text-xs text-muted flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="truncate">{narration}</span>
          <span aria-hidden className="w-[3px] h-[3px] rounded-full bg-line-2 shrink-0" />
          <span>
            {postedBy} · {time}
          </span>
        </p>
      </div>

      <div className="text-right num tabular-nums text-sm font-semibold text-bad whitespace-nowrap">
        −{paiseToInr(amountPaise)}
        <span className="block text-xs text-muted font-normal mt-0.5">{time}</span>
      </div>

      <div>
        <span
          className={cn(
            "inline-flex items-center h-[20px] px-1.5 text-xs font-semibold rounded-full border whitespace-nowrap",
            requiresFounderReview
              ? "bg-warn-50 text-warn border-warn-100"
              : "bg-raised text-muted border-line",
          )}
        >
          {requiresFounderReview ? "Founder approved" : "Posted"}
        </span>
      </div>
    </Link>
  );
}
